import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, ScanLine } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/** Where a scan goes: the scan page of the link printed in a sheet's QR (job and stage kept), else a BPR / MPWO or
 * job number as typed or read by a handheld scanner. */
export function scanTarget(raw) {
  const text = (raw || "").trim();
  if (!text) return null;
  try {
    const url = new URL(text);
    if (url.pathname.startsWith("/s/")) return `${url.pathname}${url.search}`;
  } catch { /* not a link: a number */ }
  const bpr = text.match(/mpwo[\s-]*(\d{6,})(\([A-Za-z]\))?/i);
  const number = bpr ? `MPWO-${bpr[1]}${bpr[2] ? bpr[2].toUpperCase() : ""}` : text.toUpperCase();
  return `/s/${encodeURIComponent(number)}`;
}

const READER_ID = "scan-camera";

/** Scan a sheet's QR with the device camera, or with a handheld scanner: these act as a keyboard, typing what
 * they read and pressing Enter into the box, which has the focus when this opens. */
export default function ScanDialog({ open, onOpenChange }) {
  const nav = useNavigate();
  const [text, setText] = useState("");
  const [camera, setCamera] = useState(false);
  const [error, setError] = useState("");
  const reader = useRef(null);

  const go = (raw) => {
    const to = scanTarget(raw);
    if (!to) return;
    onOpenChange(false);
    nav(to);
  };

  const stopCamera = async () => {
    const r = reader.current;
    reader.current = null;
    try { if (r?.isScanning) await r.stop(); r?.clear(); } catch { /* already stopped */ }
  };

  useEffect(() => {
    if (!open) { setText(""); setCamera(false); setError(""); stopCamera(); }
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open || !camera) return undefined;
    let off = false;
    (async () => {
      try {
        const { Html5Qrcode } = await import("html5-qrcode");
        if (off) return;
        const r = new Html5Qrcode(READER_ID);
        reader.current = r;
        await r.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 240, height: 240 } },
          (decoded) => { if (!off) { off = true; stopCamera(); go(decoded); } }, () => {});
      } catch (e) {
        setError("The camera couldn't start. Allow camera access for this site in the browser, or type the BPR number instead.");
        setCamera(false);
      }
    })();
    return () => { off = true; stopCamera(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, camera]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-sm" data-testid="scan-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><ScanLine size={18} /> Scan a job sheet</DialogTitle>
          <DialogDescription>Point the camera at the QR on a sheet, or scan it with a handheld scanner (the box below has the cursor). A BPR number typed here works too.</DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); go(text); }} className="flex gap-2">
          <input autoFocus value={text} onChange={(e) => setText(e.target.value)} data-testid="scan-input"
            placeholder="Scan here, or type a BPR number"
            className="flex-1 border border-neutral-300 rounded-sm px-3 py-2 text-sm font-mono-num outline-none focus:border-[#FF5A00]" />
          <Button type="submit" className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]" disabled={!text.trim()}>Open</Button>
        </form>
        {error && <div className="text-xs text-rose-700" data-testid="scan-error">{error}</div>}
        {!camera ? (
          <Button type="button" variant="outline" className="rounded-sm" onClick={() => { setError(""); setCamera(true); }} data-testid="scan-camera-btn">
            <Camera size={14} className="mr-1.5" /> Use the camera
          </Button>
        ) : (
          <div>
            <div id={READER_ID} className="w-full overflow-hidden rounded-sm border border-neutral-200" data-testid="scan-camera" />
            <button type="button" className="mt-2 text-xs text-neutral-500 underline" onClick={() => setCamera(false)}>Stop the camera</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
